import {
  Checkbox,
  FormFeedbackRAC,
  Icon,
  InputGroup,
  InputGroupDropDown,
  Link,
  List,
  ListItem,
  NumberFieldFormikField,
  Radio,
  RadioGroup,
  RadioGroupFormikField,
  SectionMessage,
  TextField,
  TextInputFormikField,
  Toggle,
} from "@certn/cascadia-react";
import { Stack, Text } from "@certn/cascadia";
import { formatCurrency } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";

import { PASS_THROUGH_ACCESS_FEES_URL } from "../../../constants";
import {
  AUSTRALIAN_CRIMINAL_OPTION,
  CCRC_LEVEL,
  CRIMINAL_RECORD_VALIDATION,
  US_CRIMINAL_PREFERENCE,
} from "../constants";
import { useUsCriminalCatalogPrices } from "../hooks";
import {
  LOCKED_AUSTRALIAN_ORDERING,
  type AustralianOrderingFlags,
  type CriminalRecordTemplateFormValues,
  type UsCriminalChecks,
} from "../types";
import {
  checkArgumentsToUsPreference,
  cloneUsCriminalChecks,
  coerceCurrentAddressUsPreference,
  formatMaximumPrices,
  formatUsCriminalCatalogCheckLabel,
  formatUsCriminalTierOptionLabel,
  isUsCriminalCustomCheckboxSelected,
  lookbackYearsMinimum,
  templateUsesSevenYearCourt,
  toggleUsCriminalCustomCheckbox,
  US_CRIMINAL_CHECK_TYPE,
  US_CRIMINAL_CUSTOM_CHECKBOX,
  US_CRIMINAL_TIER_CHECKS,
  usCriminalGwlCheckboxState,
  usCriminalSsnCheckboxState,
  usPreferenceToCheckArguments,
  type UsCriminalCourtVariant,
  type UsCriminalCustomCheckbox,
} from "../utils";

interface CriminalRecordTemplateFormFieldsProps {
  isDisabled?: boolean;
  showNameField?: boolean;
  values: CriminalRecordTemplateFormValues;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => void;
  allowedCurrencies: Array<{ id: string; name: string }>;
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  enableMaximumPrice: boolean;
  setEnableMaximumPrice: (enabled: boolean) => void;
  australianOrderingFlags?: AustralianOrderingFlags;
  showAustralianPreferences?: boolean;
  showUsPreferences?: boolean;
  showPriceControls?: boolean;
  isUsCatalogPricingVisible?: boolean;
}

export function CriminalRecordTemplateFormFields(
  props: CriminalRecordTemplateFormFieldsProps
) {
  const {
    isDisabled = false,
    showNameField = true,
    values,
    setFieldValue,
    allowedCurrencies,
    selectedCurrency,
    setSelectedCurrency,
    enableMaximumPrice,
    setEnableMaximumPrice,
    australianOrderingFlags = LOCKED_AUSTRALIAN_ORDERING,
    showAustralianPreferences = false,
    showUsPreferences = false,
    showPriceControls = true,
    isUsCatalogPricingVisible = showPriceControls,
  } = props;

  const { t } = useTranslation();
  const usCriminalCatalog = useUsCriminalCatalogPrices(
    showUsPreferences && isUsCatalogPricingVisible
  );
  const fromPrefix = t("From ");
  const usTierLabel = (label: string, checks: UsCriminalChecks) =>
    isUsCatalogPricingVisible
      ? formatUsCriminalTierOptionLabel(
          label,
          checks,
          usCriminalCatalog,
          fromPrefix
        )
      : label;
  const usCheckLabel = (label: string, checkType: string) =>
    isUsCatalogPricingVisible
      ? formatUsCriminalCatalogCheckLabel(
          label,
          checkType,
          usCriminalCatalog,
          fromPrefix
        )
      : label;
  const canSelectStandardNcchc =
    australianOrderingFlags.is_unlocked &&
    australianOrderingFlags.enable_ordering_standard;
  const canSelectVolunteerNcchc =
    australianOrderingFlags.is_unlocked &&
    australianOrderingFlags.enable_ordering_volunteer;
  const usCriminalPreference =
    values.us_criminal_preference ?? US_CRIMINAL_PREFERENCE.NONE;
  const usCriminalChecks = useMemo(
    () => values.us_criminal_checks ?? {},
    [values.us_criminal_checks]
  );
  const ssnCheckboxState = usCriminalSsnCheckboxState(usCriminalChecks);
  const gwlCheckboxState = usCriminalGwlCheckboxState(usCriminalChecks);
  const isCountySevenYear = isUsCriminalCustomCheckboxSelected(
    usCriminalChecks,
    US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_SEVEN_YEAR
  );
  const isCountySelected =
    isCountySevenYear ||
    isUsCriminalCustomCheckboxSelected(
      usCriminalChecks,
      US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_CURRENT
    );
  const isFederalSevenYear = isUsCriminalCustomCheckboxSelected(
    usCriminalChecks,
    US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_SEVEN_YEAR
  );
  const isFederalSelected =
    isFederalSevenYear ||
    isUsCriminalCustomCheckboxSelected(
      usCriminalChecks,
      US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_CURRENT
    );

  const setUsCriminalChecks = (
    checkbox: UsCriminalCustomCheckbox,
    selected: boolean
  ) => {
    setFieldValue(
      "us_criminal_checks",
      toggleUsCriminalCustomCheckbox(usCriminalChecks, checkbox, selected)
    );
  };

  const handleUsPreferenceChange = (preference: string) => {
    const nextPreference = checkArgumentsToUsPreference({
      us_criminal_preference: preference,
    });
    if (nextPreference === US_CRIMINAL_PREFERENCE.CUSTOM) {
      setFieldValue(
        "us_criminal_checks",
        cloneUsCriminalChecks(US_CRIMINAL_TIER_CHECKS.TIER_2)
      );
      return;
    }
    setFieldValue(
      "us_criminal_checks",
      usPreferenceToCheckArguments(nextPreference).us_criminal_checks
    );
  };

  const isCurrentAddressTemplate = Boolean(values.is_current_address);
  const lookbackMinYears = lookbackYearsMinimum(
    usCriminalPreference,
    usCriminalChecks
  );
  const currentAddressCourtLabel = isCurrentAddressTemplate
    ? t("Current address only")
    : t("Most recent US address");
  const tier2FederalBullet = isCurrentAddressTemplate
    ? t(
        "US Federal Criminal Court Record Check — ordered for the applicant's current address"
      )
    : t(
        "US Federal Criminal Court Record Check — ordered for the applicant's most recent US address"
      );
  const tier2CountyBullet = isCurrentAddressTemplate
    ? t(
        "US County Criminal Court Record Check — ordered for the applicant's current address"
      )
    : t(
        "US County Criminal Court Record Check — ordered for the applicant's most recent US address"
      );

  useEffect(() => {
    if (isDisabled || !isCurrentAddressTemplate) {
      return;
    }
    if (
      usCriminalPreference !== US_CRIMINAL_PREFERENCE.TIER_3 &&
      usCriminalPreference !== US_CRIMINAL_PREFERENCE.CUSTOM
    ) {
      return;
    }
    const coerced = coerceCurrentAddressUsPreference(
      usCriminalPreference,
      usCriminalChecks
    );
    setFieldValue("us_criminal_preference", coerced.us_criminal_preference);
    setFieldValue("us_criminal_checks", coerced.us_criminal_checks);
  }, [
    isCurrentAddressTemplate,
    isDisabled,
    setFieldValue,
    usCriminalChecks,
    usCriminalPreference,
  ]);

  useEffect(() => {
    if (isDisabled || isCurrentAddressTemplate) {
      return;
    }
    if (
      templateUsesSevenYearCourt(usCriminalPreference, usCriminalChecks) &&
      (values.lookback_years ?? 0) < lookbackMinYears
    ) {
      setFieldValue("lookback_years", lookbackMinYears);
    }
  }, [
    isCurrentAddressTemplate,
    isDisabled,
    lookbackMinYears,
    setFieldValue,
    usCriminalChecks,
    usCriminalPreference,
    values.lookback_years,
  ]);

  return (
    <>
      {showNameField && (
        <>
          <Text style="form/section-heading">{t("Template details")}</Text>
          <TextInputFormikField
            name="name"
            isRequired
            label={t("Template name")}
            testID="template-name"
            isDisabled={false}
          />
        </>
      )}

      <div className={styles.infoBox}>
        <Stack gap="space-2">
          <Stack gap="space-2" orientation="horizontal">
            <Icon name="info" size="space-4" color="icon-info" />
            <Text style="global/button-medium" color="text-base">
              {t("Address-based ordering coverage")}
            </Text>
          </Stack>
          <Text style="global/body-secondary">
            <Trans>
              Not all countries have formal criminal record processes. View the
              full list of countries to see which ones support address-based
              ordering.
            </Trans>
          </Text>
          <Text style="global/body-secondary">
            {showUsPreferences ? (
              <Trans>
                Please note: US criminal checks are configured in Country
                Preferences below.
              </Trans>
            ) : (
              <Trans>
                Please note: US criminal checks aren't included in this check
                type. If needed, they can be ordered separately, as they consist
                of several distinct searches.
              </Trans>
            )}
          </Text>
        </Stack>
      </div>

      {!values.is_current_address && (
        <>
          <Text style="form/section-heading">{t("Residency Rules")}</Text>
          <Text style="input/helper" color="text-weak">
            {t(
              "Define how long an applicant must have lived in a country within a set time window to trigger a criminal check. Example: '6 months in the past 5 years' means we'll only run it if the applicant lived in a country for at least 6 months consecutively, during that 5-year look back period."
            )}
          </Text>
          <NumberFieldFormikField
            name="check_arguments.umbrella_minimum_address_duration_months"
            minValue={CRIMINAL_RECORD_VALIDATION.MIN_DURATION_MONTHS}
            maxValue={CRIMINAL_RECORD_VALIDATION.MAX_DURATION_MONTHS}
            defaultValue={CRIMINAL_RECORD_VALIDATION.DEFAULT_DURATION_MONTHS}
            label={t("Minimum length of stay (months)")}
            testID="minimum-duration"
            isRequired
            description={t("Default is {{months}} months.", {
              months: CRIMINAL_RECORD_VALIDATION.DEFAULT_DURATION_MONTHS,
            })}
            isDisabled={isDisabled}
          />
          <NumberFieldFormikField
            name="lookback_years"
            minValue={
              isDisabled
                ? CRIMINAL_RECORD_VALIDATION.MIN_LOOKBACK_YEARS
                : lookbackMinYears
            }
            maxValue={CRIMINAL_RECORD_VALIDATION.MAX_LOOKBACK_YEARS}
            defaultValue={CRIMINAL_RECORD_VALIDATION.DEFAULT_LOOKBACK_YEARS}
            label={t("Look back period years")}
            testID="lookback-period"
            isRequired
            isDisabled={isDisabled}
          />
          <Toggle
            isSelected={
              values.check_arguments
                ?.umbrella_apply_residency_rules_to_current_address ?? true
            }
            onChange={(selected) => {
              setFieldValue(
                "check_arguments.umbrella_apply_residency_rules_to_current_address",
                selected
              );
            }}
            testID="apply-residency-rules-to-current-address"
            isDisabled={isDisabled}
          >
            {t("Apply residency rules to current address")}
          </Toggle>
        </>
      )}

      {showPriceControls && (
        <>
          <Text style="form/section-heading">{t("Price Controls")}</Text>
          <Text style="input/helper" color="text-weak">
            {t(
              "The maximum total price for all countries triggered by this template. If the estimated prices exceed this threshold, checks will be ordered starting from the applicant's most recent addresses until the limit is reached."
            )}
          </Text>
          <Toggle
            isSelected={enableMaximumPrice}
            onChange={(selected) => {
              setEnableMaximumPrice(selected);
              if (!selected) {
                setFieldValue("check_arguments.maximum_price", {});
              }
            }}
            testID="enable-maximum-price"
            isDisabled={isDisabled}
          >
            {t("Enable maximum price cap")}
          </Toggle>
          {enableMaximumPrice && (
            <>
              {isDisabled ? (
                <TextField
                  label={t("Price threshold")}
                  value={formatMaximumPrices(
                    values.check_arguments?.maximum_price
                  )}
                  isDisabled
                  testID="maximum-price-readonly"
                />
              ) : (
                <InputGroup
                  name={`check_arguments.maximum_price.${selectedCurrency}`}
                  label={t("Price threshold")}
                  type="text"
                  placeholder="0.00"
                  testID="maximum-price-input"
                  value={
                    values.check_arguments?.maximum_price?.[selectedCurrency]
                      ? formatCurrency(
                          values.check_arguments?.maximum_price[
                            selectedCurrency
                          ] ?? 0,
                          undefined,
                          selectedCurrency,
                          0
                        )
                      : ""
                  }
                  onChange={(value) => {
                    const numericValue = value.replace(/[^0-9.]/g, "");
                    setFieldValue(
                      `check_arguments.maximum_price.${selectedCurrency}`,
                      parseFloat(numericValue) || 0
                    );
                  }}
                  dropdown={
                    <InputGroupDropDown
                      selectedKey={(() => {
                        const symbol = formatCurrency(
                          0,
                          undefined,
                          selectedCurrency,
                          0
                        )
                          .replace("0", "")
                          .trim();
                        return `${selectedCurrency} ${symbol}`;
                      })()}
                      onSelectionChange={(value) => {
                        setSelectedCurrency(value as string);
                      }}
                      items={allowedCurrencies.map((currency) => {
                        const symbol = formatCurrency(
                          0,
                          undefined,
                          currency.id,
                          0
                        )
                          .replace("0", "")
                          .trim();
                        return {
                          value: currency.id,
                          display: `${currency.id} ${symbol}`,
                        };
                      })}
                      testID="currency-dropdown"
                    />
                  }
                />
              )}
              {showUsPreferences &&
                usCriminalPreference !== US_CRIMINAL_PREFERENCE.NONE && (
                  <SectionMessage
                    type="warning"
                    icon="warning"
                    sectionTitle={t(
                      "Price threshold for US Criminal Record Checks"
                    )}
                    testID="us-criminal-price-cap-warning"
                  >
                    {t(
                      "US Criminal Record Checks ordered by address don't have a fixed price — court and county access fees vary and are passed through directly. Because of this, the actual cost per order may exceed the price threshold you've set."
                    )}
                  </SectionMessage>
                )}
            </>
          )}
        </>
      )}

      <Text style="form/section-heading">{t("Country Preferences")}</Text>
      <Text style="input/helper" color="text-weak">
        {t(
          "Configure how checks should run for specific countries that require extra choices."
        )}
      </Text>
      <Text style="global/button-medium">{t("Canada")}</Text>
      <RadioGroupFormikField
        name="check_arguments.canadian_criminal_preference"
        ariaLabel={t("Canada")}
        testID="ccrc-level"
        isDisabled={isDisabled}
      >
        <Radio
          testID="ccrc-basic"
          value={CCRC_LEVEL.BASIC}
          description={t(
            "Search of national databases for summary-level criminal records."
          )}
          isDisabled={isDisabled}
        >
          {t("Basic Canadian Criminal Record Check (CCRC)")}
        </Radio>
        <Radio
          testID="ccrc-enhanced"
          value={CCRC_LEVEL.ENHANCED}
          description={t(
            "Includes additional detail, such as local police information."
          )}
          isDisabled={isDisabled}
        >
          {t("Enhanced Canadian Criminal Record Check (CCRC)")}
        </Radio>
      </RadioGroupFormikField>
      {showAustralianPreferences && (
        <>
          <Text style="global/button-medium">{t("Australia")}</Text>
          {!australianOrderingFlags.is_unlocked && (
            <div
              className={styles.infoBox}
              data-testid="australian-ncchc-accreditation-info"
            >
              <Stack gap="space-2">
                <Stack gap="space-2" orientation="horizontal">
                  <Icon name="info" size="space-4" color="icon-info" />
                  <Text style="global/body-secondary">
                    <Trans
                      i18nKey="Australia Nationally Coordinated Criminal History Check (NCCHC) options require additional accreditation. Email <email>sales@certn.co</email> to start the accreditation process for your account."
                      components={{
                        email: (
                          <Link
                            href="mailto:sales@certn.co"
                            textStyle="global/body-secondary-link"
                            testID="australian-ncchc-accreditation-email"
                          />
                        ),
                      }}
                    />
                  </Text>
                </Stack>
              </Stack>
            </div>
          )}
          <RadioGroupFormikField
            name="australian_criminal_option"
            ariaLabel={t("Australia")}
            testID="australian-criminal-option"
            isDisabled={isDisabled}
          >
            <Radio
              testID="australian-afp"
              value={AUSTRALIAN_CRIMINAL_OPTION.AFP}
              description={t(
                "Searches Commonwealth offence records nationally. Suited to general pre-employment and visa purposes."
              )}
              isDisabled={isDisabled}
            >
              {t("Australian Federal Police (AFP) National Police Check")}
            </Radio>
            <Radio
              testID="australian-standard-ncchc"
              value={AUSTRALIAN_CRIMINAL_OPTION.STANDARD_NCCHC}
              description={t(
                "Searches state, territory, and federal police records. Shows spent and unspent convictions, cautions, and relevant police information (includes barred list check if applicable)."
              )}
              isDisabled={isDisabled || !canSelectStandardNcchc}
            >
              {t(
                "Standard Australian Nationally Coordinated Criminal History Check (NCCHC)"
              )}
            </Radio>
            <Radio
              testID="australian-volunteer-ncchc"
              value={AUSTRALIAN_CRIMINAL_OPTION.VOLUNTEER_NCCHC}
              description={t(
                "Same coverage as the Standard NCCHC, issued at a reduced fee for applicants in unpaid volunteer roles."
              )}
              isDisabled={isDisabled || !canSelectVolunteerNcchc}
            >
              {t(
                "Volunteer Australian Nationally Coordinated Criminal History Check (NCCHC)"
              )}
            </Radio>
          </RadioGroupFormikField>
        </>
      )}
      {showUsPreferences && (
        <>
          <Text style="global/button-medium">{t("United States")}</Text>
          <Text style="input/helper" color="text-weak">
            {t(
              "US criminal checks may require additional county and/or federal checks to be added during fulfillment. By selecting a US check, you acknowledge that these additional checks may be added and charged even if the total cost exceeds your configured cost threshold."
            )}
          </Text>
          <RadioGroupFormikField
            name="us_criminal_preference"
            ariaLabel={t("United States")}
            testID="us-criminal-preference"
            isDisabled={isDisabled}
            onChange={handleUsPreferenceChange}
          >
            <Radio
              testID="us-criminal-none"
              value={US_CRIMINAL_PREFERENCE.NONE}
              description={t(
                "Exclude United States Criminal checks from address-based ordering"
              )}
              isDisabled={isDisabled}
            >
              {t("None")}
            </Radio>
            <Radio
              testID="us-criminal-tier-1"
              value={US_CRIMINAL_PREFERENCE.TIER_1}
              description={t(
                "This is the baseline bundle: identity/address trace plus national-level database searches, with no county or federal court record orders."
              )}
              options={
                <UsCriminalPreferenceBullets
                  testID="us-criminal-tier-1-bullets"
                  items={[
                    t("US SSN Trace"),
                    t("US National Criminal Database"),
                    t("US National Sex Offender Registry"),
                    t("Global Watchlist and Sanctions"),
                  ]}
                />
              }
              isDisabled={isDisabled}
            >
              {usTierLabel(
                t("US Tier 1 (Core)"),
                US_CRIMINAL_TIER_CHECKS.TIER_1
              )}
            </Radio>
            <Radio
              testID="us-criminal-tier-2"
              value={US_CRIMINAL_PREFERENCE.TIER_2}
              description={t("Includes everything in Tier 1, plus:")}
              options={
                <UsCriminalPreferenceBullets
                  testID="us-criminal-tier-2-bullets"
                  items={[tier2FederalBullet, tier2CountyBullet]}
                />
              }
              isDisabled={isDisabled}
            >
              {usTierLabel(
                t("US Tier 2 (Complete)"),
                US_CRIMINAL_TIER_CHECKS.TIER_2
              )}
            </Radio>
            <Radio
              testID="us-criminal-tier-3"
              value={US_CRIMINAL_PREFERENCE.TIER_3}
              description={t("Includes everything in Tier 1, plus:")}
              options={
                <UsCriminalPreferenceBullets
                  testID="us-criminal-tier-3-bullets"
                  items={[
                    t(
                      "US Federal Criminal Court Record Check — ordered across 7 years of address history"
                    ),
                    t(
                      "US County Criminal Court Record Check — ordered across 7 years of address history"
                    ),
                  ]}
                />
              }
              isDisabled={isDisabled || isCurrentAddressTemplate}
            >
              {usTierLabel(
                t("US Tier 3 (Complete +)"),
                US_CRIMINAL_TIER_CHECKS.TIER_3
              )}
            </Radio>
            <Radio
              testID="us-criminal-custom"
              value={US_CRIMINAL_PREFERENCE.CUSTOM}
              description={t(
                "Select individual checks and configure address history for court records."
              )}
              isDisabled={isDisabled || isCurrentAddressTemplate}
            >
              {t("Custom")}
            </Radio>
          </RadioGroupFormikField>
          {usCriminalPreference === US_CRIMINAL_PREFERENCE.CUSTOM && (
            <Stack gap="space-2" className={styles.nestedRadios}>
              <Checkbox
                testID="us-criminal-ncd"
                isSelected={isUsCriminalCustomCheckboxSelected(
                  usCriminalChecks,
                  US_CRIMINAL_CUSTOM_CHECKBOX.NCD
                )}
                isDisabled={isDisabled}
                onChange={(selected) =>
                  setUsCriminalChecks(US_CRIMINAL_CUSTOM_CHECKBOX.NCD, selected)
                }
              >
                {usCheckLabel(
                  t("US National Criminal Database"),
                  US_CRIMINAL_CHECK_TYPE.NCD
                )}
              </Checkbox>
              <Checkbox
                testID="us-criminal-nsor"
                isSelected={isUsCriminalCustomCheckboxSelected(
                  usCriminalChecks,
                  US_CRIMINAL_CUSTOM_CHECKBOX.NSOR
                )}
                isDisabled={isDisabled}
                onChange={(selected) =>
                  setUsCriminalChecks(
                    US_CRIMINAL_CUSTOM_CHECKBOX.NSOR,
                    selected
                  )
                }
              >
                {usCheckLabel(
                  t("US National Sex Offender Registry"),
                  US_CRIMINAL_CHECK_TYPE.NSOR
                )}
              </Checkbox>
              <UsCriminalCourtCheckOption
                label={usCheckLabel(
                  t("US County Criminal Court Record Check"),
                  US_CRIMINAL_CHECK_TYPE.COUNTY
                )}
                checkboxTestID="us-criminal-county"
                radioGroupTestID="us-criminal-county-ordering"
                currentAddressRadioLabel={currentAddressCourtLabel}
                isDisabled={isDisabled}
                isSelected={isCountySelected}
                variant={isCountySevenYear ? "seven_year" : "current"}
                onToggle={(selected) => {
                  if (selected) {
                    setUsCriminalChecks(
                      US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_CURRENT,
                      true
                    );
                    return;
                  }
                  setUsCriminalChecks(
                    isCountySevenYear
                      ? US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_SEVEN_YEAR
                      : US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_CURRENT,
                    false
                  );
                }}
                onVariantChange={(variant) =>
                  setUsCriminalChecks(
                    variant === "seven_year"
                      ? US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_SEVEN_YEAR
                      : US_CRIMINAL_CUSTOM_CHECKBOX.COUNTY_CURRENT,
                    true
                  )
                }
              />
              <UsCriminalCourtCheckOption
                label={usCheckLabel(
                  t("US Federal Criminal Court Record Check"),
                  US_CRIMINAL_CHECK_TYPE.FEDERAL
                )}
                checkboxTestID="us-criminal-federal"
                radioGroupTestID="us-criminal-federal-ordering"
                currentAddressRadioLabel={currentAddressCourtLabel}
                isDisabled={isDisabled}
                isSelected={isFederalSelected}
                variant={isFederalSevenYear ? "seven_year" : "current"}
                onToggle={(selected) => {
                  if (selected) {
                    setUsCriminalChecks(
                      US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_CURRENT,
                      true
                    );
                    return;
                  }
                  setUsCriminalChecks(
                    isFederalSevenYear
                      ? US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_SEVEN_YEAR
                      : US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_CURRENT,
                    false
                  );
                }}
                onVariantChange={(variant) =>
                  setUsCriminalChecks(
                    variant === "seven_year"
                      ? US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_SEVEN_YEAR
                      : US_CRIMINAL_CUSTOM_CHECKBOX.FEDERAL_CURRENT,
                    true
                  )
                }
              />
              <Checkbox
                testID="us-criminal-ssn"
                isSelected={ssnCheckboxState.isSelected}
                isDisabled={isDisabled || ssnCheckboxState.isDisabled}
                onChange={(selected) =>
                  setUsCriminalChecks(US_CRIMINAL_CUSTOM_CHECKBOX.SSN, selected)
                }
              >
                {usCheckLabel(t("US SSN Trace"), US_CRIMINAL_CHECK_TYPE.SSN)}
              </Checkbox>
              <Checkbox
                testID="us-criminal-gwl"
                isSelected={gwlCheckboxState.isSelected}
                isDisabled={isDisabled || gwlCheckboxState.isDisabled}
                onChange={(selected) =>
                  setUsCriminalChecks(US_CRIMINAL_CUSTOM_CHECKBOX.GWL, selected)
                }
              >
                {usCheckLabel(
                  t("Global Watchlist and Sanctions (add-on only)"),
                  US_CRIMINAL_CHECK_TYPE.GWL
                )}
              </Checkbox>
              {!isDisabled && Object.keys(usCriminalChecks).length === 0 && (
                <FormFeedbackRAC
                  type="invalid"
                  testID="us-criminal-custom-empty-error"
                >
                  {t("Select at least one US criminal check.")}
                </FormFeedbackRAC>
              )}
            </Stack>
          )}
          {isUsCatalogPricingVisible && (
            <Text style="input/helper" color="text-weak">
              <Trans
                i18nKey="*Additional court pass-through fees may apply. <learnMore>Learn more</learnMore>"
                components={{
                  learnMore: (
                    <Link
                      href={PASS_THROUGH_ACCESS_FEES_URL}
                      textStyle="global/body-secondary-link"
                      isNewTab
                      testID="us-criminal-pass-through-fees-link"
                    />
                  ),
                }}
              />
            </Text>
          )}
        </>
      )}
    </>
  );
}

const styles = {
  infoBox: css({
    backgroundColor: "var(--background-info-weak)",
    padding: "var(--space-6)",
    borderRadius: "var(--border-radius-sm)",
  }),
  nestedRadios: css({
    paddingLeft: "var(--space-8)",
  }),
};

function UsCriminalPreferenceBullets({
  items,
  testID,
}: {
  items: string[];
  testID: string;
}) {
  return (
    <List type="unordered" size="secondary" testID={testID}>
      {items.map((item) => (
        <ListItem key={item} color="text-weak">
          {item}
        </ListItem>
      ))}
    </List>
  );
}

function UsCriminalCourtCheckOption({
  label,
  checkboxTestID,
  radioGroupTestID,
  currentAddressRadioLabel,
  isDisabled,
  isSelected,
  variant,
  onToggle,
  onVariantChange,
}: {
  label: string;
  checkboxTestID: string;
  radioGroupTestID: string;
  currentAddressRadioLabel: string;
  isDisabled: boolean;
  isSelected: boolean;
  variant: UsCriminalCourtVariant;
  onToggle: (selected: boolean) => void;
  onVariantChange: (nextVariant: UsCriminalCourtVariant) => void;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-1">
      <Checkbox
        testID={checkboxTestID}
        isSelected={isSelected}
        isDisabled={isDisabled}
        onChange={onToggle}
      >
        {label}
      </Checkbox>
      {isSelected && (
        <div className={styles.nestedRadios}>
          <RadioGroup
            aria-label={label}
            value={variant}
            onChange={(value) =>
              onVariantChange(value as UsCriminalCourtVariant)
            }
            isDisabled={isDisabled}
            testID={radioGroupTestID}
          >
            <Radio
              testID={`${checkboxTestID}-current`}
              value="current"
              isDisabled={isDisabled}
            >
              {currentAddressRadioLabel}
            </Radio>
            <Radio
              testID={`${checkboxTestID}-seven-year`}
              value="seven_year"
              isDisabled={isDisabled}
            >
              {t("7 years of address history")}
            </Radio>
          </RadioGroup>
        </div>
      )}
    </Stack>
  );
}
